import { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  AspectRatio,
  Image,
  Stack,
  HStack,
  Heading,
  Box,
  Button,
  Text,
  Show,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import {
  UserAction,
  NestedHome,
  NestedProject,
  NestedSearch,
  SellerActionData,
  isSellerEvent,
} from '@habity/types';
import { ClaimedProperty } from '~/components/ui/svg';
import { useAgency } from '~/hooks/useAgency';
import { getStreet } from '@habity/services';
import { postAPI, getAPI } from '~/lib/api';
import { useTracking } from '~/hooks/useTracking';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '~/store';
import { revalidateHomeViewing } from '~/app/actions/revalidateViewing';
import { revalidateProjectViewing } from '~/app/actions/revalidateProjectViewing';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { SITE_URL } from '~/constants/app';
import {
  SignupForm,
  SignupFormValues,
  UseFormReturn,
} from '~/components/ui/forms/SignupForm';
import { useToast } from '~/lib/toasts';
import { useUser } from '~/hooks/useUser';
import { FloatingLabelInput } from '~/components/ui/forms/FloatingLabelInput';
import validators from '~/lib/forms/rhf-validators';
import { useForm } from 'react-hook-form';
import { ActionConfirmation } from './ActionConfirmation';
import {
  ViewingParticipantErrorCode,
  VIEWING_ERROR_CONFIG,
} from '@habity/constants/errors';
import { API_PATHS } from '@habity/constants';
import { PageFooter } from '~/components/ui/page/footer';
import { getImageUrl } from '@habity/services';

interface TwoStepEnhancedLoginFormProps {
  authHome: NestedHome | null;
  authProject: NestedProject | null;
  authAction: UserAction | null;
  redirectTo?: string;
  sellerActionData?: SellerActionData;
  /** Search filters for created_search_profile action */
  searchFilters?: Partial<NestedSearch>;
  onSuccess?: (result: any) => void | Promise<void>;
  /** Whether to use sticky footer on mobile (e.g., in seller onboarding) */
  useStickyFooterOnMobile?: boolean;
  /** Whether to include home status in translation strings (default: auto - true only for premarket homes) */
  includeHomeStatusInTranslations?: boolean;
  /** Whether to hide the header (logo and title) - useful when embedded in other flows */
  hideHeader?: boolean;
  /** Whether to show the valuation request checkbox */
  showValuationCheckbox?: boolean;
  /** Whether to show the back button in step 2 */
  showBackButton?: boolean;
}

interface StepOneFormValues extends Record<string, unknown> {
  email: string;
}

interface UserLookupResult {
  exists: boolean;
  hasCompleteProfile: boolean;
}

export const TwoStepEnhancedLoginForm = ({
  authHome,
  authProject,
  authAction,
  redirectTo,
  sellerActionData,
  searchFilters,
  onSuccess,
  useStickyFooterOnMobile = false,
  includeHomeStatusInTranslations,
  hideHeader = false,
  showValuationCheckbox = true,
  showBackButton = false,
}: TwoStepEnhancedLoginFormProps) => {
  // Auto-determine if we should include home status in translations
  // Default: only include for premarket homes, unless explicitly overridden
  const shouldIncludeStatus =
    includeHomeStatusInTranslations ?? authHome?.status?.name === 'premarket';

  // Show valuation checkbox if there's a responsible broker OR for search profile creation
  const canShowValuationCheckbox =
    showValuationCheckbox &&
    (!!authHome?.responsible_broker || authAction === 'created_search_profile');

  const searchParams = useSearchParams();
  const { agency, theme, getLogoSrc } = useAgency();
  const { showToast } = useToast();
  const { identify } = useTracking();
  const authI18n = useTranslations('Auth');
  const loginI18n = useTranslations('Login');

  const { loginViaViewingSlotId, setModals, modals } = useUIStore(
    (state) => ({
      loginViaViewingSlotId: state.loginViaViewingSlotId,
      setModals: state.setModals,
      modals: state.modals,
    }),
    shallow
  );

  // State management
  const [currentStep, setCurrentStep] = useState<1 | 2 | 'confirmation'>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [userLookupResult, setUserLookupResult] =
    useState<UserLookupResult | null>(null);
  const [completedEmail, setCompletedEmail] = useState<string>('');
  const [completedValuation, setCompletedValuation] = useState<number | null>(
    null
  );

  // Get current user for prepopulating email
  const { user: currentUser } = useUser();

  // Get email from URL params or current user
  const emailFromUrl = searchParams?.get('email') || '';

  // Form references
  const stepOneFormRef = useRef<HTMLFormElement>(null);
  const stepTwoFormRef = useRef<UseFormReturn<SignupFormValues>>(null);

  // Step 1 form methods
  const stepOneForm = useForm<StepOneFormValues>({
    defaultValues: { email: emailFromUrl || currentUser?.email || '' },
    mode: 'onSubmit',
  });

  // Update email field when currentUser loads (for logged-in users)
  useEffect(() => {
    if (currentUser?.email && !stepOneForm.getValues('email')) {
      stepOneForm.setValue('email', currentUser.email);
    }
  }, [currentUser?.email, stepOneForm]);

  const {
    register: registerStepOne,
    handleSubmit: handleSubmitStepOne,
    formState: { errors: errorsStepOne, isSubmitting: isSubmittingStepOne },
  } = stepOneForm;

  // Check if user exists and has complete profile
  const checkUserProfile = async (email: string): Promise<UserLookupResult> => {
    try {
      const response = await postAPI(
        API_PATHS.PROVIDERS.SUPABASE.USERS.LOOKUP,
        JSON.stringify({ email, agency_id: agency?.id })
      );

      if (response.error) {
        return { exists: false, hasCompleteProfile: false };
      }

      return {
        exists: response.exists,
        hasCompleteProfile: response.hasCompleteProfile,
      };
    } catch (error) {
      console.error('Error checking user profile:', error);
      return { exists: false, hasCompleteProfile: false };
    }
  };

  // Step 1 submission - check user profile
  const onStepOneSubmit = async (data: StepOneFormValues) => {
    setIsLoading(true);
    try {
      const lookupResult = await checkUserProfile(data.email);
      setUserLookupResult(lookupResult);

      if (lookupResult.exists && lookupResult.hasCompleteProfile) {
        // User exists with complete profile - perform action directly
        await performActionWithExistingUser(data.email);
      } else {
        // User doesn't exist or profile incomplete - go to step 2
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Error in step one:', error);
      showToast(
        {
          title: 'Något gick fel',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Perform action with existing user email
  const performActionWithExistingUser = async (email: string) => {
    try {
      // Check if this is a seller event
      const isSellerAction = authAction && isSellerEvent(authAction);

      if (isSellerAction && sellerActionData) {
        // Handle seller actions
        const requestData = {
          email: email,
          userData: {
            email: email,
            agency_id: agency?.id,
            app_mode: 'seller',
            source: 'seller_onboarding',
          },
          action: authAction,
          sellerActionData,
        };

        const result = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify(requestData)
        );

        if (result.error) {
          throw new Error(result.message || 'Ett fel uppstod');
        }

        // Identify user in frontend PostHog for subsequent page tracking
        if (result.user) {
          identify(result.user);
        }

        // Note: Action event tracking handled in backend

        // Store valuation if returned
        if (result.valuation) {
          setCompletedValuation(result.valuation);
        }

        // Call custom success handler if provided
        if (onSuccess) {
          await onSuccess(result);
        }

        setCompletedEmail(email);
        setCurrentStep('confirmation');
        return;
      }

      // Handle project actions (non-viewing) - routing determined by authProject being present
      // Note: Project viewing bookings now use the same flow as home viewings
      // (useProjectViewing resolves the home viewing slot and passes it via executeAction)
      if (authAction && authProject) {
        // First create/update the user
        const userResult = await postAPI(
          '/providers/supabase/users',
          JSON.stringify({
            email: email,
            agency_id: agency?.id,
          })
        );

        if (userResult.error) {
          throw new Error(userResult.message || 'Ett fel uppstod');
        }

        // Then create the project interest
        const interestResult = await postAPI(
          '/providers/supabase/project-interests',
          JSON.stringify({
            project_id: authProject.id,
            user_id: userResult.userId,
            interest_level: authAction,
            user_event: authAction,
            origin: 'internal',
          })
        );

        if (interestResult.error) {
          throw new Error(interestResult.message || 'Ett fel uppstod');
        }

        // Note: Skip identify here since we only have userId -
        // user will be identified when they navigate to authenticated pages

        setCompletedEmail(email);
        setCurrentStep('confirmation');
        return;
      }

      // Handle search profile creation (no home/project required)
      if (authAction === 'created_search_profile') {
        const searchResponse = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify({
            email,
            userData: { email, agency_id: agency?.id, app_mode: 'buyer' },
            action: authAction,
            searchFilters: searchFilters || {},
          })
        );

        if (searchResponse.error) {
          throw new Error(searchResponse.message || 'Ett fel uppstod');
        }

        if (searchResponse?.user) identify(searchResponse.user);
        setCompletedEmail(email);
        setCurrentStep('confirmation');

        // Call onSuccess callback if provided
        if (onSuccess) {
          await onSuccess(searchResponse);
        }
        return;
      }

      // Handle home actions - routing determined by authHome being present
      if (authAction && authHome) {
        const requestData: {
          email: string;
          userData: Record<string, unknown>;
          action: UserAction;
          homeId: number;
          viewingId?: number;
          viewingSlotId?: number;
        } = {
          email: email,
          userData: {
            email: email,
            agency_id: agency?.id,
          },
          action: authAction,
          homeId: authHome.id,
        };

        if (authAction === 'booked_viewing' && loginViaViewingSlotId) {
          requestData.viewingSlotId = loginViaViewingSlotId;
        }

        const result = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify(requestData)
        );

        if (result.error) {
          throw new Error(result.message || 'Ett fel uppstod');
        }

        // Check for viewing-specific error codes
        if (result.errorCode && authAction === 'booked_viewing') {
          const errorConfig =
            VIEWING_ERROR_CONFIG[
            result.errorCode as ViewingParticipantErrorCode
            ];
          if (errorConfig) {
            showToast(
              {
                title: errorConfig.title,
                description: result.errorMessage || errorConfig.description,
              },
              { status: errorConfig.status }
            );
            return; // Don't proceed to confirmation
          }
        }

        // Identify user in frontend PostHog for subsequent page tracking
        if (result.user) {
          identify(result.user);
        }

        // Note: Action event tracking handled in backend

        if (authAction === 'booked_viewing' && authHome) {
          try {
            await revalidateHomeViewing(authHome);
          } catch (revalidateError) {
            console.warn('Failed to revalidate home viewing:', revalidateError);
          }
        }

        // Send magic link email with redirect URL (only for non-logged-in users)
        // Use redirectTo if provided, otherwise default to current page
        if (!currentUser) {
          try {
            const currentPath =
              typeof window !== 'undefined' ? window.location.pathname : '/';

            const redirectTarget = redirectTo || currentPath;

            const redirectUrl = `${SITE_URL}/auth/continue?next=${encodeURIComponent(
              redirectTarget
            )}`;

            await getAPI(
              `/providers/customerio/user/magiclink/${email}?redirectTo=${encodeURIComponent(
                redirectUrl
              )}`
            );
          } catch (error) {
            console.warn('Failed to send magic link:', error);
          }
        }

        // Show confirmation screen
        setCompletedEmail(email);
        setCurrentStep('confirmation');
      }
    } catch (error) {
      console.error('Error performing action with existing user:', error);
      throw error;
    }
  };

  // Step 2 submission - complete profile and perform action
  const onStepTwoSubmit = async (data: SignupFormValues) => {
    setIsLoading(true);
    try {
      let firstName = data.name;
      let lastName = '';

      const nameParts = data.name.trim().split(' ');
      if (nameParts.length > 1) {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }

      // Parse phone number
      const parsedPhone = data.phone
        ? parsePhoneNumber(data.phone, { regionCode: 'SE' })
        : null;
      const formattedPhone = parsedPhone?.valid
        ? parsedPhone.number.e164
        : data.phone;

      const termsAcceptedAt = data.acceptTerms
        ? new Date().toISOString()
        : null;

      const privacyPolicyAcceptedAt = data.acceptPrivacyPolicy
        ? new Date().toISOString()
        : null;

      const loanContactRequestedAt = data.loanContactRequest
        ? new Date().toISOString()
        : null;

      const loanContactRequestDeniedAt = data.loanContactRequest
        ? null
        : new Date().toISOString();

      const userData = {
        email: data.email,
        name: data.name,
        first_name: firstName,
        last_name: lastName,
        phone: formattedPhone,
        terms_accepted_at: termsAcceptedAt,
        privacy_policy_accepted_at: privacyPolicyAcceptedAt,
        loan_contact_accepted_at: loanContactRequestedAt,
        loan_contact_denied_at: loanContactRequestDeniedAt,
        agency_id: agency?.id,
      };

      // Check if this is a seller event
      const isSellerAction = authAction && isSellerEvent(authAction);

      if (isSellerAction && sellerActionData) {
        // Handle seller actions for new users
        const requestData = {
          email: data.email,
          userData: {
            ...userData,
            app_mode: 'seller',
            source: 'seller_onboarding',
          },
          action: authAction,
          sellerActionData,
          valuationRequest: data.valuationRequest,
          brokerId: authHome?.responsible_broker?.id,
          homeStatus: authHome?.status?.name,
        };

        const result = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify(requestData)
        );

        if (result.error) {
          throw new Error(result.message || 'Ett fel uppstod');
        }

        // Identify user in frontend PostHog for subsequent page tracking
        if (result.user) {
          identify(result.user);
        }

        // Note: Action event tracking handled in backend

        // Store valuation if returned
        if (result.valuation) {
          setCompletedValuation(result.valuation);
        }

        // Call custom success handler if provided
        if (onSuccess) {
          await onSuccess(result);
        }

        setCompletedEmail(data.email);
        setCurrentStep('confirmation');
        return;
      }

      // Handle project actions (non-viewing) - routing determined by authProject being present
      // Note: Project viewing bookings now use the same flow as home viewings
      // (useProjectViewing resolves the home viewing slot and passes it via executeAction)
      if (authAction && authProject) {
        // First create/update the user
        const userResult = await postAPI(
          '/providers/supabase/users',
          JSON.stringify({
            email: data.email,
            userData,
          })
        );

        if (userResult.error) {
          throw new Error(userResult.message || 'Ett fel uppstod');
        }

        // Then create the project interest
        const interestResult = await postAPI(
          '/providers/supabase/project-interests',
          JSON.stringify({
            project_id: authProject.id,
            user_id: userResult.userId,
            interest_level: authAction,
            user_event: authAction,
            origin: 'internal',
          })
        );

        if (interestResult.error) {
          throw new Error(interestResult.message || 'Ett fel uppstod');
        }

        // Note: Skip identify here since we only have userId -
        // user will be identified when they navigate to authenticated pages

        setCompletedEmail(data.email);
        setCurrentStep('confirmation');
        return;
      }

      // Handle search profile creation for new users (no home/project required)
      if (authAction === 'created_search_profile') {
        const searchResponse = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify({
            email: data.email,
            userData: { ...userData, app_mode: 'buyer' },
            action: authAction,
            searchFilters: searchFilters || {},
          })
        );

        if (searchResponse.error) {
          throw new Error(searchResponse.message || 'Ett fel uppstod');
        }

        if (searchResponse?.user) identify(searchResponse.user);
        stepTwoFormRef.current?.reset();
        setCompletedEmail(data.email);
        setCurrentStep('confirmation');

        // Call onSuccess callback if provided
        if (onSuccess) {
          await onSuccess(searchResponse);
        }
        return;
      }

      // Handle home actions - routing determined by authHome being present
      if (authAction && authHome) {
        const requestData: {
          email: string;
          userData: Record<string, unknown>;
          action: UserAction;
          homeId: number;
          viewingId?: number;
          viewingSlotId?: number;
          valuationRequest?: boolean;
          brokerId?: number;
          homeStatus?: string;
        } = {
          email: data.email,
          userData,
          action: authAction,
          homeId: authHome.id,
          valuationRequest: data.valuationRequest,
          brokerId: authHome.responsible_broker?.id,
          homeStatus: authHome.status?.name,
        };

        if (authAction === 'booked_viewing' && loginViaViewingSlotId) {
          requestData.viewingSlotId = loginViaViewingSlotId;
        }

        const result = await postAPI(
          API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
          JSON.stringify(requestData)
        );

        if (result.error) {
          throw new Error(result.message || 'Ett fel uppstod');
        }

        // Check for viewing-specific error codes
        if (result.errorCode) {
          const errorConfig =
            VIEWING_ERROR_CONFIG[
            result.errorCode as ViewingParticipantErrorCode
            ];
          if (errorConfig) {
            showToast(
              {
                title: errorConfig.title,
                description: result.errorMessage || errorConfig.description,
              },
              { status: errorConfig.status }
            );
            return; // Don't proceed to confirmation
          }
        }

        if (authAction === 'booked_viewing' && authHome) {
          try {
            await revalidateHomeViewing(authHome);
          } catch (revalidateError) {
            console.warn('Failed to revalidate home viewing:', revalidateError);
          }
        }

        // Send magic link email with redirect URL (only for non-logged-in users)
        // Use redirectTo if provided, otherwise default to current page
        if (!currentUser) {
          try {
            const currentPath =
              typeof window !== 'undefined' ? window.location.pathname : '/';

            const redirectTarget = redirectTo || currentPath;

            const redirectUrl = `${SITE_URL}/auth/continue?next=${encodeURIComponent(
              redirectTarget
            )}`;
            await getAPI(
              `/providers/customerio/user/magiclink/${data.email
              }?redirectTo=${encodeURIComponent(redirectUrl)}`
            );
          } catch (error) {
            console.warn('Failed to send magic link:', error);
          }
        }

        stepTwoFormRef.current?.reset();

        // Show confirmation screen
        setCompletedEmail(data.email);
        setCurrentStep('confirmation');
        return;
      }

      showToast({
        title: 'Registrering genomförd',
        description: 'Tack för din registrering!',
      });

      stepTwoFormRef.current?.reset();
      setIsLoading(false);
    } catch (error) {
      console.error('Error processing action:', error);
      showToast(
        {
          title: 'Något gick fel',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
      setIsLoading(false);
    }
  };

  const getLoginLogo = (
    home?: NestedHome | null,
    project?: NestedProject | null
  ) => {
    if (home) {
      const { images } = home || {};
      const hasImages = images && images.length > 0;
      return (
        <AspectRatio ratio={1} w={currentStep === 2 ? '50px' : '120px'}>
          <Box borderRadius="full" overflow="hidden">
            <Image
              borderRadius="full"
              border="1px solid #000"
              src={
                hasImages
                  ? getImageUrl(images[0], 'list')
                  : '/eo/images/eo-home-missing-img.jpg'
              }
            />
          </Box>
        </AspectRatio>
      );
    }

    if (project) {
      const { images } = project || {};
      return images && images.length > 0 ? (
        <AspectRatio ratio={1} w={currentStep === 2 ? '50px' : '120px'}>
          <Box borderRadius="full" overflow="hidden">
            <Image
              borderRadius="full"
              border="1px solid #000"
              src={getImageUrl(images[0] as any, 'list')}
            />
          </Box>
        </AspectRatio>
      ) : (
        <ClaimedProperty w={currentStep === 2 ? '50px' : '120px'} />
      );
    }

    // Hide logo for search profile creation
    if (authAction === 'created_search_profile') {
      return null;
    }

    return (
      <Image src={getLogoSrc()} maxW={theme?.logo_max_width} pt={3} pb={1} />
    );
  };

  const getLoginTitle = (
    home?: NestedHome,
    project?: NestedProject,
    action?: UserAction
  ) => {
    if (!action) return undefined;

    const status = shouldIncludeStatus ? home?.status?.name : undefined;

    // Check if action has nested status-specific translations
    const hasNestedTranslations =
      status && loginI18n.has(`Title.${action}.${status}`);
    const key = hasNestedTranslations
      ? `Title.${action}.${status}`
      : `Title.${action}`;

    // If action is an object (nested), use default or status-specific key
    const finalKey =
      typeof loginI18n.raw(key) === 'object'
        ? `${key}.${status || 'default'}`
        : key;

    const isSellerEventAction = isSellerEvent(action);

    let displayName;
    if (project) {
      displayName =
        project.name || project.heading || project.city || 'detta projekt';
    } else if (home) {
      displayName = getStreet(home);
    } else {
      displayName = isSellerEventAction
        ? loginI18n('Title.Fallback.yourHome')
        : loginI18n('Title.Fallback.dreamHomes');
    }

    return loginI18n(finalKey, {
      street: displayName,
    });
  };

  const getActionButtonCTA = (action: UserAction | null, home?: NestedHome) => {
    if (!action) return loginI18n('Button.default');
    try {
      const key = `Button.${action}`;

      const status = shouldIncludeStatus ? home?.status?.name : undefined;

      // If action is an object (nested), use default or status-specific key
      const finalKey =
        typeof loginI18n.raw(key) === 'object'
          ? `${key}.${status || 'default'}`
          : key;

      return loginI18n(finalKey);
    } catch {
      return loginI18n('Button.default');
    }
  };

  const goBackToStepOne = () => {
    setCurrentStep(1);
    setUserLookupResult(null);
  };

  if (!agency) return null;

  const actualLoading = isLoading || isSubmittingStepOne;

  // Show confirmation screen
  if (currentStep === 'confirmation' && authAction) {
    // Handle search profile creation confirmation (no home/project required)
    if (authAction === 'created_search_profile') {
      return (
        <ActionConfirmation
          action={authAction}
          email={completedEmail}
          user={currentUser}
          onClose={() => {
            setModals({
              ...modals,
              enhancedLoginOpen: {
                isOpen: false,
                home: null,
                project: null,
                action: null,
                redirectTo: undefined,
              },
            });
          }}
        />
      );
    }

    // For project viewings, show project confirmation (not home confirmation)
    // even though we pass authHome for the API call
    if (authProject) {
      return (
        <ActionConfirmation
          action={authAction}
          project={authProject}
          email={completedEmail}
          user={currentUser}
          onClose={() => {
            setModals({
              ...modals,
              enhancedLoginOpen: {
                isOpen: false,
                home: null,
                project: null,
                action: null,
                redirectTo: undefined,
              },
            });
          }}
        />
      );
    }

    if (authHome) {
      const home = authHome;

      return (
        <ActionConfirmation
          action={authAction}
          home={home}
          email={completedEmail}
          homeStatus={authHome?.status?.name}
          valuation={completedValuation}
          user={currentUser}
          onClose={() => {
            setModals({
              ...modals,
              enhancedLoginOpen: {
                isOpen: false,
                home: null,
                project: null,
                action: null,
                redirectTo: undefined,
              },
            });
          }}
        />
      );
    }
  }

  return (
    <Box
      pt={hideHeader ? '0' : { base: '8', sm: '8' }}
      pb={hideHeader ? { base: '6', sm: '8' } : { base: '8', sm: '8' }}
      px={hideHeader ? '0' : { base: '10', sm: '10' }}
    >
      <Stack spacing={hideHeader ? '4' : currentStep === 2 ? '4' : '8'}>
        {!hideHeader && (
          <Stack spacing="6">
            {/* Mobile: Use same layout as desktop per step */}
            <Show below="md">
              {currentStep === 2 ? (
                // Step 2: Horizontal layout
                <HStack spacing="4" align="center">
                  {/* Image on the left */}
                  {authHome || authProject ? (
                    <Box flexShrink={0}>
                      {getLoginLogo(authHome, authProject)}
                    </Box>
                  ) : (
                    <Box flexShrink={0}>{getLoginLogo()}</Box>
                  )}

                  {/* Title on the right */}
                  <Stack spacing="2" flex="1">
                    <Heading size="md">
                      {getLoginTitle(
                        authHome || undefined,
                        authProject || undefined,
                        authAction || undefined
                      ) || authI18n('magic_link.title')}
                    </Heading>
                  </Stack>
                </HStack>
              ) : (
                // Step 1: Vertical layout (same as desktop)
                <Stack spacing="4" align="center">
                  {/* Image on top */}
                  {authHome || authProject
                    ? getLoginLogo(authHome, authProject)
                    : getLoginLogo()}

                  {/* Title below */}
                  <Stack spacing="3" textAlign="center">
                    <Heading size="lg">
                      {getLoginTitle(
                        authHome || undefined,
                        authProject || undefined,
                        authAction || undefined
                      ) || authI18n('magic_link.title')}
                    </Heading>
                  </Stack>
                </Stack>
              )}
            </Show>

            {/* Desktop: Match mobile layout (image next to title) on step 2, keep vertical on other steps */}
            <Show above="md">
              {currentStep === 2 ? (
                // Step 2: Horizontal layout like mobile
                <HStack spacing="4" align="center">
                  {/* Image on the left */}
                  {authHome || authProject ? (
                    <Box flexShrink={0}>
                      {getLoginLogo(authHome, authProject)}
                    </Box>
                  ) : (
                    <Box flexShrink={0}>{getLoginLogo()}</Box>
                  )}

                  {/* Title on the right */}
                  <Stack spacing="2" flex="1">
                    <Heading size="md">
                      {getLoginTitle(
                        authHome || undefined,
                        authProject || undefined,
                        authAction || undefined
                      ) || authI18n('magic_link.title')}
                    </Heading>
                  </Stack>
                </HStack>
              ) : (
                // Other steps: keep existing vertical layout
                <Stack spacing="4" align="center">
                  {/* Image on top */}
                  {authHome || authProject
                    ? getLoginLogo(authHome, authProject)
                    : getLoginLogo()}

                  {/* Title below */}
                  <Stack spacing="3" textAlign="center">
                    <Heading size="lg">
                      {getLoginTitle(
                        authHome || undefined,
                        authProject || undefined,
                        authAction || undefined
                      ) || authI18n('magic_link.title')}
                    </Heading>
                  </Stack>
                </Stack>
              )}
            </Show>
          </Stack>
        )}

        {!hideHeader &&
          currentStep === 1 &&
          authAction &&
          (() => {
            const actionKey = `Description.${authAction}`;

            if (!loginI18n.has(actionKey)) return null;

            const description = loginI18n.markup(actionKey as any, {
              strong: (chunks) => `<strong>${chunks}</strong>`,
            });

            return (
              <Stack justifyContent="center">
                <Text
                  size="sm"
                  color="blackAlpha.700"
                  align={{
                    base: authHome ? 'left' : 'center',
                    md: 'center',
                  }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </Stack>
            );
          })()}

        <Stack spacing="2">
          {currentStep === 1 ? (
            // Step 1: Email input
            <Box
              as="form"
              onSubmit={handleSubmitStepOne(onStepOneSubmit)}
              rounded="md"
              backgroundColor="white"
            >
              <Stack spacing="4">
                <FloatingLabelInput
                  label="E-postadress"
                  placeholder="din@email.se"
                  isInvalid={!!errorsStepOne.email}
                  error={errorsStepOne.email?.message}
                  {...registerStepOne('email', validators.email())}
                  isDisabled={actualLoading}
                  isRequired
                  useFloatingLabel
                  autoFocus
                />

                {useStickyFooterOnMobile ? (
                  <>
                    {/* Desktop button */}
                    <Show above="tablet">
                      <Button
                        type="submit"
                        width="full"
                        isLoading={actualLoading}
                        mt={2}
                        bg="blackAlpha.900"
                        color="white"
                        _hover={{ bg: 'blackAlpha.700' }}
                      >
                        Fortsätt
                      </Button>
                    </Show>

                    {/* Mobile button - stick to bottom */}
                    <Show below="tablet">
                      <PageFooter>
                        <Button
                          type="submit"
                          width="100%"
                          isLoading={actualLoading}
                          bg="blackAlpha.900"
                          color="white"
                          _hover={{ bg: 'blackAlpha.700' }}
                        >
                          Fortsätt
                        </Button>
                      </PageFooter>
                    </Show>
                  </>
                ) : (
                  <Button
                    type="submit"
                    width="full"
                    isLoading={actualLoading}
                    mt={2}
                    bg="blackAlpha.900"
                    color="white"
                    _hover={{ bg: 'blackAlpha.700' }}
                  >
                    Fortsätt
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            // Step 2: Complete profile form
            <Stack spacing="4">
              <Stack spacing="2">
                {showBackButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goBackToStepOne}
                    alignSelf="flex-start"
                    leftIcon={<Text>←</Text>}
                    isDisabled={isLoading}
                  >
                    Tillbaka
                  </Button>
                )}

                <Text fontSize="sm" color="gray.600" mb={2}>
                  Vi behöver lite mer information för att slutföra din
                  registrering.
                </Text>
              </Stack>

              <SignupForm
                formRef={stepTwoFormRef}
                onSubmit={onStepTwoSubmit}
                defaultValues={{
                  email: stepOneForm.getValues('email'),
                  name: '',
                  phone: '',
                  acceptTerms: false,
                  acceptPrivacyPolicy: false,
                  loanContactRequest: false,
                  valuationRequest: false,
                }}
                disabledFields={{
                  email: true, // Email is already entered in step 1
                }}
                isLoading={isLoading}
                submitButtonLabel={getActionButtonCTA(
                  authAction,
                  authHome || undefined
                )}
                useStickyFooterOnMobile={useStickyFooterOnMobile}
                showNameField={true}
                showPhoneField={true}
                showValuationCheckbox={canShowValuationCheckbox}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
